import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import type { DynamicTableViewMode } from './dynamic-table-types';

interface GlobalFilterController {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export interface DynamicTableHeaderProps {
  globalFilterEnabled: boolean;
  globalFilterController?: GlobalFilterController;
  toolbar?: ReactNode;
  toolbarActions?: ReactNode;
  viewModeSwitchEnabled: boolean;
  viewMode: DynamicTableViewMode;
  setViewMode: (mode: DynamicTableViewMode) => void;
  kanbanEnabled: boolean;
  paginationEnabled: boolean;
  totalRecords: number;
  pageSize: number;
  pageSizeOptions: readonly number[];
  onPageSizeChange?: (size: number) => void;
}

export function DynamicTableHeader(props: DynamicTableHeaderProps) {
  const {
    globalFilterEnabled,
    globalFilterController,
    toolbar,
    toolbarActions,
    viewModeSwitchEnabled,
    viewMode,
    setViewMode,
    kanbanEnabled,
    paginationEnabled,
    totalRecords,
    pageSize,
    pageSizeOptions,
    onPageSizeChange,
  } = props;

  if (
    !globalFilterEnabled &&
    !toolbar &&
    !toolbarActions &&
    !viewModeSwitchEnabled &&
    // When pagination is enabled, still show header even if totalRecords = 0
    // so users can see "Total: 0" and change page size.
    !(paginationEnabled && onPageSizeChange)
  ) {
    return null;
  }

  return (
    <div className="p-4 border-b border-border space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        {globalFilterEnabled && globalFilterController && (
          <input
            type="search"
            placeholder={globalFilterController.placeholder ?? 'Search...'}
            value={globalFilterController.value}
            onChange={e => globalFilterController.onChange(e.target.value)}
            className="flex-1 min-w-[200px] px-4 py-2 bg-background border border-border rounded-md focus:ring-2 focus:ring-primary/20"
          />
        )}
        {toolbar}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <span className="text-sm text-muted-foreground">
          Total: <strong className="text-foreground">{totalRecords}</strong> record
          {totalRecords === 1 ? '' : 's'}
        </span>
        <div className="flex items-center gap-2 ml-auto">
          {paginationEnabled && onPageSizeChange && (
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              Page size:{' '}
              <select
                value={pageSize}
                onChange={e => onPageSizeChange(Number(e.target.value))}
                className="h-9 px-3 rounded-md border border-border bg-background text-foreground text-sm focus:ring-2 focus:ring-primary/20 focus:outline-none"
              >
                {pageSizeOptions.map(size => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
          )}
          {toolbarActions && (
            <div className="flex items-center gap-2 border-l border-border pl-2 ml-1">
              {toolbarActions}
            </div>
          )}
          {viewModeSwitchEnabled && (
            <div
              className="flex items-center gap-1 border-l border-border pl-2 ml-1"
              role="group"
              aria-label="View mode"
            >
              <Button
                type="button"
                variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-9 px-2.5"
                onClick={() => setViewMode('list')}
                title="List view"
              >
                List
              </Button>
              <Button
                type="button"
                variant={viewMode === 'card' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-9 px-2.5"
                onClick={() => setViewMode('card')}
                title="Card view"
              >
                Card
              </Button>
              <Button
                type="button"
                variant={viewMode === 'kanban' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-9 px-2.5"
                onClick={() => setViewMode('kanban')}
                title="Kanban view"
                disabled={!kanbanEnabled}
              >
                Kanban
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
